// Interface and organizational functions

import _ from "lodash";

export interface IField {
  field: string;
  display?: string;
  required?: boolean;
  type?: string;
  hint?: string;
}

export interface ITemplate {
  name: string;
  fields: IField[];
}

const templatesFromArray = (templateArray: ITemplate[]) => {
  const templates = templateArray.reduce((obj, template, index) => {
    return { ...obj, [index]: template.fields };
  }, {});
  const options = templateArray.map((template, index) => {
    return {
      label: template.name,
      value: index,
    };
  });
  return {
    templates,
    options,
    templateArray,
  };
};

// Fields for each template type
// -----------------------------
const textFields: IField[] = [
  { field: "message", display: "Message", required: true },
  { field: "pictogram", display: "Pictogram" },
  { field: "backgroundColor", display: "Background Colour", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const textAndImageFields: IField[] = [
  { field: "title", display: "Title" },
  { field: "caption", display: "Text", required: true },
  { field: "imageUrl", display: "Image", required: true },
  { field: "backgroundColor", display: "Background Colour", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const textAndImageFieldsWithSubheading: IField[] = [
  { field: "title", display: "Title" },
  { field: "subheading", display: "Subheading" },
  { field: "caption", display: "Text", required: true },
  { field: "imageUrl", display: "Image", required: true },
  { field: "backgroundColor", display: "Background Colour", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const textAndVideoFields: IField[] = [
  { field: "title", display: "Title" },
  { field: "caption", display: "Text", required: true },
  { field: "videoUrl", display: "Video file", required: true },
  { field: "backgroundColor", display: "Background Colour", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const textAndVideoFieldsWithSubheading: IField[] = [
  { field: "title", display: "Title" },
  { field: "subheading", display: "Subheading" },
  { field: "caption", display: "Text", required: true },
  { field: "videoUrl", display: "Video file", required: true },
  { field: "backgroundColor", display: "Background Colour", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const fullscreenImageFields: IField[] = [
  { field: "imageUrl", display: "Image", required: true },
  { field: "duration", display: "Duration" },
  { field: "displayDates", display: "Display Dates" },
];

const fullscreenVideoFields: IField[] = [
  { field: "videoUrl", display: "Video", required: true },
  { field: "displayDates", display: "Display Dates" },
];

const donorListFields: IField[] = [
  { field: "subheading", display: "Donor Type", required: true },
  { field: "caption", display: "Donor Description", required: false },
  { field: "donorList", display: "Donor List" },
  { field: "displayDates", display: "Display Dates" },
  { field: "backgroundColor", display: "Background Colour", required: true },
];

// Templates for each screen type
// ------------------------------
const fourScreenTemplateArray: ITemplate[] = [
  { name: "Fullscreen Image", fields: fullscreenImageFields },
  { name: "Fullscreen Video", fields: fullscreenVideoFields },
  { name: "Text", fields: textFields },
  { name: "Text & Image - 1", fields: textAndImageFields },
  { name: "Text & Image - 2", fields: textAndImageFields },
  { name: "Text & Video - 1", fields: textAndVideoFields },
  { name: "Text & Video - 2", fields: textAndVideoFields },
];

const largeScreenTemplateArray: ITemplate[] = [
  { name: "Fullscreen Image", fields: fullscreenImageFields },
  { name: "Fullscreen Video", fields: fullscreenVideoFields },
  { name: "Text", fields: textFields },
  { name: "Text & Image - 1", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Image - 2", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Video - 1", fields: textAndVideoFieldsWithSubheading },
  { name: "Text & Video - 2", fields: textAndVideoFieldsWithSubheading },
];

const smallScreenTemplateArray: ITemplate[] = [
  { name: "Fullscreen Image", fields: fullscreenImageFields },
  { name: "Fullscreen Video", fields: fullscreenVideoFields },
  { name: "Text", fields: textFields },
  { name: "Text & Image - 1", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Image - 2", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Video - 1", fields: textAndVideoFieldsWithSubheading },
  { name: "Text & Video - 2", fields: textAndVideoFieldsWithSubheading },
];

const donorTemplateArray: ITemplate[] = [
  ...smallScreenTemplateArray,
  { name: "Donor List", fields: donorListFields },
];

const testerArray: ITemplate[] = [
  { name: "Fullscreen Image", fields: fullscreenImageFields },
  { name: "Fullscreen Video", fields: fullscreenVideoFields },
  { name: "Text", fields: textFields },
  { name: "Text & Image - 1 (4 screen)", fields: textAndImageFields },
  { name: "Text & Image - 2 (4 screen)", fields: textAndImageFields },
  { name: "Text & Video - 1 (4 screen)", fields: textAndVideoFields },
  { name: "Text & Video - 2 (4 screen)", fields: textAndVideoFields },
  { name: "Text & Image - 1", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Image - 2", fields: textAndImageFieldsWithSubheading },
  { name: "Text & Video - 1", fields: textAndVideoFieldsWithSubheading },
  { name: "Text & Video - 2", fields: textAndVideoFieldsWithSubheading },
];

const fourScreenTemplates = templatesFromArray(fourScreenTemplateArray);
const largeTemplates = templatesFromArray(largeScreenTemplateArray);
const smallTemplates = templatesFromArray(smallScreenTemplateArray);
const donorTemplates = templatesFromArray(donorTemplateArray);

// Screen types
// ------------
const screenTypesArray = [
  { id: 1, name: "Main Reception", data: fourScreenTemplates },
  { id: 2, name: "Lift Lobby", data: largeTemplates },
  { id: 3, name: "Central Waiting Area", data: smallTemplates },
  { id: 4, name: "School Reception", data: smallTemplates },
  { id: 501, name: "Donors Left", data: smallTemplates },
  { id: 502, name: "Donors Right", data: donorTemplates },
  { id: 6, name: "Workplace Social LG", data: largeTemplates },
  { id: 7, name: "Workplace Social L1", data: largeTemplates },
  { id: 8, name: "Business Hub", data: smallTemplates },
  { id: 900, name: "Tester", data: templatesFromArray(testerArray) },
];

const screenTypes: any = screenTypesArray.reduce((obj, screen) => {
  return {
    ...obj,
    [screen.id]: { name: screen.name, ...screen.data },
  };
}, {});

const getScreenData = (id: number | undefined) =>
  screenTypesArray.find((st) => st.id === id);

const screenTypeDropdowns = screenTypesArray.map((screen) => {
  return {
    model: "slides",
    field: `templateType-${screen.id}`,
    options: screen.data.options,
  };
});

const validateResponse = (valid: boolean, message: string) => {
  return {
    valid,
    message,
  };
};

const validateFields = (
  screenId: number,
  itemData: any
): { valid: boolean; message: string } => {
  const screen = screenTypes[screenId] || null;
  if (!screen) return validateResponse(false, "Invalid screen");
  if (!itemData) return validateResponse(false, "No item data");
  if (itemData.templateType === undefined)
    return validateResponse(false, "No template selected");

  const template = screen.templateArray[itemData.templateType];
  if (!template) return validateResponse(false, "Invalid template selected");

  const missingFields = template.fields.reduce((arr: string, field: IField) => {
    if (field.required === true) {
      const value = itemData[field.field];
      if (!value || (_.isString(value) && value.length < 1))
        return `${arr}${field.display} `;
    }
    return arr;
  }, "");

  // console.log(template.fields, missingFields);
  if (missingFields.length > 0)
    return validateResponse(
      false,
      `Missing the following required fields: ${missingFields}`
    );

  return validateResponse(true, "");
};

export {
  smallTemplates,
  largeTemplates,
  smallScreenTemplateArray,
  largeScreenTemplateArray,
  donorTemplateArray,
  donorTemplates,
  screenTypes,
  screenTypesArray,
  screenTypeDropdowns,
  getScreenData,
  validateFields,
};
